/**
 * MemoForm's task-only metadata bar — due date/time picker and pipeline stage
 * picker. Rendered only for `ahoy_task` (notes carry neither). Sits below the
 * editor and above the linked-records slot. Privacy and assignee live in the
 * MemoFormControls cluster, not here.
 *
 * Tasks only ever edit through the composer surfaces (the inline
 * record-slideout editor is the note record's), so the bar carries the
 * composer's `px-3` unconditionally.
 */

import { parseDate } from '@internationalized/date';
import type { DateValue, TimeValue } from 'react-aria-components';

import { DatePicker } from '@/lib/base/pickers/date-picker/date-picker';
import type { StageAssociationValue } from '@/lib/crm/components/properties/inputs/AssociatedPipelineStageInput';
import { AssociatedPipelineStageInput } from '@/lib/crm/components/properties/inputs/AssociatedPipelineStageInput';
import { cx } from '@/utils/cx';

import { TASK_STAGE_ASSOCIATION_TYPE_NAME } from '../task/task.constants';

export interface TaskFormMetadataBarProps {
  date: string;
  time: TimeValue | null;
  onDateChange: (value: DateValue | null) => void;
  onTimeChange: (value: TimeValue | null) => void;
  submitLock: boolean;
  hourCycle: 12 | 24;
  effectiveStageAssociation: string | null | undefined;
  onStagePickerChange: (assoc: StageAssociationValue | null) => void;
  mode: 'create' | 'edit';
}

export function TaskFormMetadataBar({
  date,
  time,
  onDateChange,
  onTimeChange,
  submitLock,
  hourCycle,
  effectiveStageAssociation,
  onStagePickerChange,
  mode,
}: TaskFormMetadataBarProps) {
  return (
    <div className='flex items-center gap-1 py-1.5 px-3 border-t border-[var(--color-border-secondary)] justify-between'>
      {/* Date: single compact DatePicker — icon-only when empty, shows
          formatted date when set. Clearable via "Clear date" in the
          calendar popover. */}
      <div className={cx('shrink-0', !date && '[&_[data-text]]:hidden')}>
        <DatePicker
          size='xs'
          borderless
          clearable
          showTime
          value={date ? parseDate(date) : null}
          onChange={onDateChange}
          timeValue={time}
          onTimeChange={onTimeChange}
          isDisabled={submitLock}
          aria-label={date ? 'Change date' : 'Add date'}
          placeholder=''
          hourCycle={hourCycle}
        />
      </div>
      {/* Stage picker. */}
      <div className='shrink-0'>
        <AssociatedPipelineStageInput
          value={effectiveStageAssociation ?? null}
          onChange={onStagePickerChange}
          entityType='ahoy_task'
          associationTypeName={TASK_STAGE_ASSOCIATION_TYPE_NAME}
          disabled={submitLock}
          size='xs'
          disableAutoSelect={mode === 'edit'}
          placement='top start'
        />
      </div>
    </div>
  );
}
